import React, { memo } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/constants';
import NoDataFound from '../Common/NoDataFound';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import DataTableLoading from '../Common/DataTableLoading';

interface Staff {
  id: number;
  name: string;
  dob: string;
  email: string;
  phone: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zip_code: string;
  my_number: string;
  created_at: string;
}

interface StaffTableProps {
  staffList: Staff[];
  isPending: boolean;
  handleDeleteStaff: (staffId: number, staffName: string) => void;
  handleSort: (field: string) => void;
  sortField: string;
  sortOrder: 'ASC' | 'DESC';
}

// Staff name component
const StaffName: React.FC<{
  staff: Staff;
}> = memo(({ staff }) => (
  <div>
    <div className='font-semibold text-gray-900 text-sm'>{staff.name}</div>
  </div>
));

StaffName.displayName = 'StaffName';

// Address component
const AddressInfo: React.FC<{
  staff: Staff;
}> = memo(({ staff }) => {
  const fullAddress = [
    staff.address_line_1,
    staff.address_line_2,
    staff.city,
    staff.zip_code
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className='text-xs text-gray-600 max-w-xs truncate'
      title={fullAddress}
    >
      {fullAddress}
    </div>
  );
});

AddressInfo.displayName = 'AddressInfo';

const ActionButtons: React.FC<{
  staff: Staff;
  isPending: boolean;
  onDelete: (staffId: number, staffName: string) => void;
}> = memo(({ staff, isPending, onDelete }) => (
  <div
    className='flex items-center gap-2'
    role='group'
    aria-label={`Actions for ${staff.name}`}
  >
    <Link
      to={`${ROUTE_PATHS.STAFF_MANAGER_ADD}/${staff.id}`}
      className='p-2 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
      title='Edit staff member - will re-generate financial data'
      aria-label={`Edit ${staff.name}`}
    >
      <Edit size={18} aria-hidden='true' />
    </Link>

    <button
      onClick={() => onDelete(staff.id, staff.name)}
      disabled={isPending}
      className='p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
      title='Delete staff member'
      aria-label={`Delete ${staff.name}`}
    >
      <Trash2 size={18} aria-hidden='true' />
    </button>
  </div>
));

ActionButtons.displayName = 'ActionButtons';

const StaffTable: React.FC<StaffTableProps> = ({
  staffList,
  isPending,
  handleDeleteStaff
}) => {
  return (
    <div
      className='bg-white relative h-[calc(100vh-160px)] rounded-xl shadow-sm border border-gray-200 overflow-hidden'
      role='region'
      aria-label='Staff list table'
    >
      <DataTable
        value={staffList}
        scrollable
        scrollHeight='calc(100vh - 160px)'
        style={{ height: '100%' }}
        tableStyle={{ minWidth: '70rem', tableLayout: 'fixed' }}
        columnResizeMode='fit'
        className='text-sm'
        sortMode='multiple'
        emptyMessage={
          <NoDataFound
            text='No staff members found'
            description='Try adjusting your search criteria or add new staff members'
          />
        }
        showGridlines
        stripedRows
        rowHover
        virtualScrollerOptions={{
          lazy: true,
          itemSize: 64,
          showLoader: true,
          loading: isPending,
          loadingTemplate: options => <DataTableLoading options={options} />
        }}
      >
        <Column
          field='name'
          header='Name'
          sortable
          body={staff => <StaffName staff={staff} />}
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />

        <Column
          field='dob'
          header='DOB'
          sortable
          body={staff => (
            <time className='text-sm text-gray-900' dateTime={staff.dob}>
              {new Date(staff.dob).toLocaleDateString()}
            </time>
          )}
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />

        <Column
          field='phone'
          header='Phone'
          sortable
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />

        <Column
          field='email'
          header={'Email'}
          body={staff => (
            <div
              className='truncate'
              title={staff.email}
              style={{ maxWidth: '100%' }}
            >
              {staff.email}
            </div>
          )}
          sortable
          style={{ width: '20%' }}
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='text-xs md:text-sm text-slate-700 py-3 px-4 border-b border-gray-100'
        />

        <Column
          field='address'
          header='Address'
          body={staff => <AddressInfo staff={staff} />}
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />

        <Column
          field='my_number'
          header='My Number'
          sortable
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />

        <Column
          field='action'
          header='Action'
          body={staff => (
            <ActionButtons
              staff={staff}
              isPending={isPending}
              onDelete={handleDeleteStaff}
            />
          )}
          headerClassName='bg-slate-50 text-slate-700 font-semibold py-3 px-4 text-xs md:text-sm border-b border-gray-200'
          bodyClassName='py-3 px-4 text-xs md:text-sm text-slate-700 border-b border-gray-100'
        />
      </DataTable>
    </div>
  );
};

export default memo(StaffTable);
